import { useCallback, useRef, useState } from "react";
import { isEmpty } from "lodash";
import isFunction from "lodash/isFunction";
import { NavLink } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import { CustomizeLayoutForm } from "@/react/components/Spaces/CustomizeSpaceFullPage/CustomizeLayoutForm";
import { CustomizeEventLayoutForm } from "@/react/components/Spaces/CustomizeSpaceFullPage/CustomizeLayoutForm/CustomizeEventLayoutForm";
import { GeneralForm } from "@/react/components/Spaces/CustomizeSpaceFullPage/GeneralForm";
import { Section } from "@/react/components/Spaces/CustomizeSpaceFullPage/Section";
import { SpaceImagesForm } from "@/react/components/Spaces/CustomizeSpaceFullPage/SpaceImagesForm";
import { EditSpaceForm } from "@/react/components/Spaces/EditV2/EditForm/EditSpaceForm";
import {
  isBasicPostsSpace,
  isChatSpace,
  isCourseSpace,
  isDraftSpace,
  isEventsSpace,
  isImagesSpace,
  isMembersSpace,
} from "@/react/helpers/spaceHelpers";
import { CustomizeTopicsForm } from "@circle-react/components/Spaces/CustomizeSpaceFullPage/CustomizeTopicsForm";
import {
  usePunditUserContext,
  useSpaceGroupsContext,
} from "@circle-react/contexts";
import {
  DIRTY_FORM_INITIAL_STATES,
  useCustomizeSpaceContext,
} from "@circle-react/contexts/customizeSpaceContext";
import type { Space } from "@circle-react/types/Space";
import { useCustomizeUIData } from "@circle-react-shared/CustomizeUI";
import { Typography } from "@circle-react-shared/uikit/Typography";
import { SectionCard } from "@circle-react-uikit/SectionCard";
import { useToast } from "@circle-react-uikit/ToastV2";
import { WelcomeBanner } from "../../WelcomeBanner/Form";
import { CustomizeTopicsOptionsForm } from "../CustomizeTopicsOptionsForm";
import { AccessSection } from "./AccessSection";
import { useConfirmSpaceGroupChangeModal } from "./ConfirmSpaceGroupChange";
import { DraftVisibilityConfirmModal } from "./DraftVisibilityConfirmModal";
import { useDraftVisibilityConfirmModal } from "./hooks/useDraftVisibilityConfirmModal";

interface CustomizeSpaceFormProps {
  space: Space;
}

export const CustomizeSpaceForm = ({ space }: CustomizeSpaceFormProps) => {
  const triggerFormSubmitRef = useRef();
  const toast = useToast();
  const [dirtyWelcomeBanner, setDirtyWelcomeBanner] = useState({
    loggedIn: false,
    loggedOut: false,
  });
  const context = useCustomizeSpaceContext();
  const { dirtyForms, setDirtyForms } = context as any;
  const { onSave: onSubmit, onExit } = useCustomizeUIData();
  const isSpaceChatSpace = isChatSpace(space);
  const isSpaceMembersSpace = isMembersSpace(space);
  const { currentCommunitySettings } = usePunditUserContext();
  const {
    space_description_enabled: isSpaceDescriptionEnabled,
    event_space_calendar_view_enabled: isEventSpaceCalendarViewEnabled,
  } = currentCommunitySettings || {};

  const shouldDisplaySpaceTopics =
    isBasicPostsSpace(space) ||
    isImagesSpace(space) ||
    isEventsSpace(space) ||
    isCourseSpace(space);

  const onDraftVisibilityConfirmModalSubmit = useCallback(() => {
    (triggerFormSubmitRef as any)?.current?.();
  }, [triggerFormSubmitRef]);

  const {
    shouldShowDraftAccessConfirmaModal,
    toggleDraftAccessConfirmaModal,
    isDraftAccessConfirmed,
    toggleIsDraftAccessConfirmed,
  } = useDraftVisibilityConfirmModal(onDraftVisibilityConfirmModalSubmit);

  const onChangeFormField = (key: string) => {
    setDirtyForms({
      ...dirtyForms,
      customize: {
        ...dirtyForms.customize,
        [key]: true,
      },
    });
  };

  const onConfirmDraftAccess = () => {
    toggleDraftAccessConfirmaModal();
    toggleIsDraftAccessConfirmed();
  };

  const setWelcomeBannerDirtyState = (key: string, value: boolean) => {
    setDirtyWelcomeBanner({
      ...dirtyWelcomeBanner,
      [key]: value,
    });
  };

  const { records: spaceGroups } = useSpaceGroupsContext();

  const doesSpaceGroupIncludedInAccessGroups = (spaceGroupId: number) => {
    const spaceGroup = spaceGroups.find(record => record.id === spaceGroupId);
    return !isEmpty(spaceGroup?.access_groups);
  };

  const spaceGroupName = (spaceGroupId: number): string => {
    const spaceGroup = spaceGroups.find(record => record.id === spaceGroupId);
    return spaceGroup?.name || "";
  };

  const confirmChangeSpaceGroupModal = useConfirmSpaceGroupChangeModal();

  const handleSubmit = (
    data: any,
    reset?: (data: any, options?: any) => void,
  ): void => {
    const { visibility } = data;
    if (dirtyWelcomeBanner.loggedIn || dirtyWelcomeBanner.loggedOut) {
      toast.error(
        t("landing_page.settings.welcome_banner.errors.blank_welcome_banner"),
      );
      return;
    }
    const shouldConfirmDraftVisibility =
      isDraftSpace(space) && visibility !== "draft" && !isDraftAccessConfirmed;

    if (shouldConfirmDraftVisibility) {
      toggleDraftAccessConfirmaModal();
      return;
    }

    const submitForm = async () => {
      await onSubmit({ data });
      isFunction(reset) && reset(data, { keepValues: true });
      setDirtyForms(DIRTY_FORM_INITIAL_STATES);
      onExit();
    };

    if (
      data.space_group_id != space.space_group_id &&
      doesSpaceGroupIncludedInAccessGroups(data.space_group_id)
    ) {
      void confirmChangeSpaceGroupModal.show({
        spaceName: data.name,
        currentSpaceGroupName: spaceGroupName(space.space_group_id),
        newSpaceGroupName: spaceGroupName(data.space_group_id),
        onConfirm: () => void submitForm(),
      });
    } else {
      void submitForm();
    }
  };

  const shouldHideImagesSection = isSpaceChatSpace || isSpaceMembersSpace;

  return (
    <Section>
      <EditSpaceForm
        onSubmit={(data: any, { reset }: any) => handleSubmit(data, reset)}
        space={space}
        className="mx-auto flex h-full flex-col"
        formClassName="flex flex-col pb-8 gap-8 md:gap-12"
        triggerFormSubmitRef={triggerFormSubmitRef}
      >
        <SectionCard
          title={t("customize_space.general")}
          dataTestId="customize-space-general"
          variant="fullScreen"
          isFirstChild
        >
          <GeneralForm
            onChange={() => onChangeFormField("general")}
            space={space}
            spaceGroups={spaceGroups}
          />
        </SectionCard>
        <AccessSection
          space={space}
          onChange={() => onChangeFormField("access")}
          onExit={onExit}
        />
        {(isBasicPostsSpace(space) ||
          isSpaceMembersSpace ||
          isImagesSpace(space)) && (
          <SectionCard
            title={t("customize_space.customize_layout")}
            dataTestId="customize-space-layout"
            variant="fullScreen"
          >
            <div className="h-full">
              <CustomizeLayoutForm
                space={space}
                onChange={() => onChangeFormField("customizeLayout")}
              />
            </div>
          </SectionCard>
        )}
        {shouldDisplaySpaceTopics && (
          <SectionCard
            title={
              isCourseSpace(space)
                ? t("customize_space.course_topics.title")
                : t("customize_space.topics.title")
            }
            dataTestId="customize-space-topics"
            variant="fullScreen"
            isFirstChild
          >
            <div className="my-1 md:mb-4">
              <Typography.BodyMd color="text-light">
                {isCourseSpace(space)
                  ? t("customize_space.course_topics.description")
                  : t("customize_space.topics.description")}
                <NavLink
                  onClick={onExit}
                  to="/settings/topics"
                  className="text-light hover:text-dark ml-0.5"
                >
                  <u>
                    {isCourseSpace(space)
                      ? t("customize_space.course_topics.manage_topics")
                      : t("customize_space.topics.manage_topics")}
                  </u>
                </NavLink>
              </Typography.BodyMd>
            </div>
            <CustomizeTopicsForm
              spaceId={space.id}
              onChange={() => onChangeFormField("topics")}
              sortable={!isCourseSpace(space)}
            />
            {!isCourseSpace(space) && (
              <CustomizeTopicsOptionsForm
                onChange={() => onChangeFormField("topics")}
              />
            )}
          </SectionCard>
        )}
        {isEventsSpace(space) && (
          <SectionCard
            title={t("customize_space.customize_layout")}
            dataTestId="customize-space-layout"
            variant="fullScreen"
          >
            <div className="h-full">
              <CustomizeEventLayoutForm
                space={space}
                isEventSpaceCalendarViewEnabled={
                  !!isEventSpaceCalendarViewEnabled
                }
                onChange={() => onChangeFormField("customizeLayout")}
              />
            </div>
          </SectionCard>
        )}
        {!shouldHideImagesSection && (
          <SectionCard
            title={t("customize_space.space_images")}
            dataTestId="customize-space-images"
            variant="fullScreen"
          >
            <SpaceImagesForm
              space={space}
              onChange={() => onChangeFormField("images")}
            />
          </SectionCard>
        )}
        {isSpaceDescriptionEnabled && isEventsSpace(space) && (
          <div id="space-welcome-banner">
            <SectionCard
              title={t("customize_space.welcome_banner.title")}
              dataTestId="customize-space-welcome-banner"
              variant="fullScreen"
            >
              <WelcomeBanner
                setDirty={setWelcomeBannerDirtyState}
                onChange={() => onChangeFormField("general")}
                space={space}
              />
            </SectionCard>
          </div>
        )}
        <DraftVisibilityConfirmModal
          isOpen={shouldShowDraftAccessConfirmaModal}
          onClose={toggleDraftAccessConfirmaModal}
          onConfirm={onConfirmDraftAccess}
          spaceType={isCourseSpace(space) ? "Course" : "Space"}
        />
      </EditSpaceForm>
    </Section>
  );
};
